import React, {useMemo, useContext, useState} from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, useMultiStyleConfig, Button, Link, Divider, Box} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import ItemList from '../../../../components/item-list'
import OrderItemCard from './order-item-card'
import {getInfoLabelVariantByOmsStatus, getOrderStatus, sortOrderItemsWithWarranty} from '../utils'
import {remCalc} from '../../../../theme/_utils'
// import {mockOrderDetail} from '../__mock__'
import {SFCC_ORDER_STATUS} from '../../constant'
import {ActionsContext, OrderContext} from '../order-context'
import {setActiveShipment} from '../order-actions'
import ReturnOrderForm from './return-order-form'
import {AlertMessage} from '../../../../components/alert-message'
import ReturnOrderModal from './return-order-modal'
import {
    useCustomPreference,
    useCustomPreferenceMarketless,
} from '../../../../hooks/use-custom-preference'
import {addDays} from '../../../../utils/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Link as ReactLink} from 'react-router-dom'
import routingObject from '../../../../utils/static-routes'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import PricingDetails from './pricing-details'
import InformativeLabel from '../../../../components/informative-label/partials/InformativeLabel'
// Lazy load the PDF generation utilities
const lazyGeneratePdfUtils = () => import('../../../../utils/pdf-utils')

const MultipleShipments = ({order, fiscalDocuments, refetchOrder}) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const handleOpenModal = () => {
        setIsModalOpen(true)
    }
    const handleCloseModal = () => {
        setIsModalOpen(false)
    }

    const {locale} = useMultiSite()
    const navigate = useNavigation()
    const handleReturnPolicy = () => {
        navigate(locale?.returnPolicy || '/Return')
    }
    const {formatDate, formatMessage} = useIntl()
    const orderItems = order?.orderItems || []
    const shipments = order?.fulfillmentOrder || []
    const returnOrders = order?.returnOrder || []
    const countrySettings = order?.countrySettings[0]
    const minimumDaysReturn = countrySettings?.MinimumDaysForReturn__c
    const maximumDaysReturn = countrySettings?.MaximumDaysForReturn__c
    const today = new Date()
    const orderType = order?.orderType
    const deliveryInfo = order?.deliveryInfo
    const deliveryCountryCode = deliveryInfo?.DeliverToCountryCode?.toLowerCase()
    const styles = useMultiStyleConfig('Orders')
    const dispatch = useContext(ActionsContext)
    const notReturnableProducts = useCustomPreferenceMarketless('showNotReturnableProducts')
    const notReturnableIds = notReturnableProducts?.split(',') || []
    const returnSettings = useCustomPreference('showReturnDetails')
    const returnsAllowed = useCustomPreferenceMarketless('showReturnsAllowed')
    const {activeReturnShipmentId} = useContext(OrderContext)
    // Helper to get extension from mime type
    const getExtensionFromMime = (mime) => {
        if (mime === 'application/pdf') return 'pdf'
        if (mime === 'image/png') return 'png'
        if (mime === 'image/jpeg') return 'jpg'
        if (mime === 'image/gif') return 'gif'
        if (mime === 'application/zip') return 'zip'
        return 'bin'
    }

    const generateReturnFormPdf = async (returnOrder) => {
        const {handlePrintReturnForm} = await lazyGeneratePdfUtils()
        await handlePrintReturnForm(
            returnOrder,
            order,
            deliveryCountryCode,
            formatMessage,
            formatDate,
        )
    }

    // Download file from data URL (barcode or returnLabel)
    const handleDownloadDataUrl = (dataUrl, filenamePrefix) => {
        if (!dataUrl) return
        const matches = dataUrl.match(/^data:(.+);base64,(.*)$/)
        if (!matches) return
        const mimeType = matches[1]
        const base64 = matches[2]
        const binaryData = atob(base64)
        const byteArray = new Uint8Array(binaryData.length)
        for (let i = 0; i < binaryData.length; i++) {
            byteArray[i] = binaryData.charCodeAt(i)
        }
        const blob = new Blob([byteArray], {type: mimeType})
        const ext = getExtensionFromMime(mimeType)
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `${filenamePrefix}.${ext}`
        link.click()
        URL.revokeObjectURL(link.href)
    }

    const handleDownloadReturnLabel = (dataUrl) => {
        handleDownloadDataUrl(dataUrl, 'return-label')
    }
    const handleDownloadBarcode = (dataUrl) => {
        handleDownloadDataUrl(dataUrl, 'barcode')
    }
    const {buildUrl} = useMultiSite()
    // Mapping shipments to order items based on OrderItemSummaryId
    const mappedProductsWithDeticatedShipment = useMemo(() => {
        if (!shipments.length || !orderItems.length) return []

        //TODO check if its better to move all the mappings below to the backend file get-order-details.js - for performance
        //TODO when a return for an order is rejected it should be listed/ordered last in the UI
        return shipments.map((shipment) => {
            const shipmentId =
                shipment?.attributes?.url?.split('/')?.pop() || shipment?.ExternalId__c

            return {
                ...shipment,
                shipmentId,
                currentReturnedOrder: returnOrders?.find((returnOrder) =>
                    shipment?.FulfillmentOrderLineItems?.records.some((lineItem) =>
                        returnOrder?.ReturnOrderLineItems?.records.some(
                            (returnLineItem) =>
                                returnLineItem.OrderItemSummaryId === lineItem?.OrderItemSummaryId,
                        ),
                    ),
                ),
                allreturnOrders: returnOrders?.filter((returnOrder) =>
                    shipment?.FulfillmentOrderLineItems?.records.some((lineItem) =>
                        returnOrder?.ReturnOrderLineItems?.records.some(
                            (returnLineItem) =>
                                returnLineItem.OrderItemSummaryId === lineItem?.OrderItemSummaryId,
                        ),
                    ),
                ),
                // returnOrderItems: orderItems.filter((item) =>
                //     order?.returnOrder?.some((order) =>
                //         order?.ReturnOrderLineItems.records.some(
                //             (lineItem) => lineItem?.OrderItemSummaryId === item?.Id,
                //         ),
                //     ),
                // ),
                fiscalDocuments: Array.isArray(fiscalDocuments)
                    ? fiscalDocuments.find(
                          (invoice) => invoice?.name?.split('_').pop() === shipment?.ExternalId__c,
                      )
                    : null,
                orderItems: (() => {
                    const lineItems = shipment?.FulfillmentOrderLineItems?.records || []
                    const lineItemIds = new Set(
                        lineItems.map((lineItem) => lineItem?.OrderItemSummaryId).filter(Boolean),
                    )

                    const decoratedItems = orderItems.map((item) => {
                        const fulfillmentItem = lineItems.find(
                            (lineItem) => lineItem.OrderItemSummaryId === item.Id,
                        )
                        const cleanedDate = fulfillmentItem?.DeliveryDate__c
                            ? fulfillmentItem.DeliveryDate__c.replace(
                                  /(\+|-)(\d{2})(\d{2})$/,
                                  '$1$2:$3',
                              )
                            : null
                        const deliveryDate = cleanedDate ? new Date(cleanedDate) : null

                        return {
                            ...item,
                            deliveryDate,
                            notReturnable: notReturnableIds.includes(item.ProductCode),
                        }
                    })

                    const assignedItems = decoratedItems.filter((item) => lineItemIds.has(item.Id))

                    return sortOrderItemsWithWarranty(assignedItems)
                })(),
            }
        })
    }, [shipments.length, orderItems.length, fiscalDocuments, notReturnableIds])

    const unassignedOrderItems = useMemo(() => {
        if (!shipments.length || !orderItems.length) return []

        const assignedOrderItemIds = new Set(
            shipments
                .flatMap((shipment) => shipment?.FulfillmentOrderLineItems?.records || [])
                .map((lineItem) => lineItem?.OrderItemSummaryId)
                .filter(Boolean),
        )

        return orderItems
            .filter((item) => !assignedOrderItemIds.has(item?.Id))
            .map((item) => ({
                ...item,
                deliveryDate: null,
                notReturnable: notReturnableIds.includes(item?.ProductCode),
            }))
    }, [shipments, orderItems, notReturnableIds])

    const unassignedPreorderItem = unassignedOrderItems.find(
        (item) =>
            (item?.PurchaseType__c === 'Preorder' || item?.PurchaseType__c === 'Backorder') &&
            item?.ExpectedAvailabilityDate__c,
    )

    // const {status: orderStatus, message: orderMessage} = getOrderStatus(order?.status)
    // const translatedOrderStatus = orderMessage && formatMessage(orderMessage)

    const activeReturnShipment = useMemo(() => {
        if (!activeReturnShipmentId) return null
        // Match by shipmentId
        let found = mappedProductsWithDeticatedShipment.find(
            (s) => s.shipmentId === activeReturnShipmentId,
        )
        if (found) return found
        // Backward compatibility: fallback on OrderItemSummaryId
        const candidates = mappedProductsWithDeticatedShipment.filter((s) =>
            s?.FulfillmentOrderLineItems?.records?.some(
                (li) => li.OrderItemSummaryId === activeReturnShipmentId,
            ),
        )
        if (candidates.length === 0) return null
        // Prefer a non-canceled shipment if duplicates exist
        const nonCanceled = candidates.find((s) => s.Status?.toLowerCase() !== 'canceled')
        return nonCanceled || candidates[0]
    }, [activeReturnShipmentId, mappedProductsWithDeticatedShipment])

    if (mappedProductsWithDeticatedShipment.length === 0) return null

    return (
        <>
            {activeReturnShipment ? (
                <ReturnOrderForm
                    order={order}
                    orderId={order?.id}
                    deliveryInfo={order?.deliveryInfo}
                    activeShipment={activeReturnShipment}
                    orderType={orderType}
                    countrySettings={countrySettings}
                    returnOrders={returnOrders}
                    // pass wrapped setter so ReturnOrderForm can reset it with null
                    setActiveShipment={(id) => setActiveShipment(dispatch, id)}
                    refetchOrder={refetchOrder}
                />
            ) : (
                <Flex flexDirection="column" {...styles.borderWrapper}>
                    {mappedProductsWithDeticatedShipment.map((shipment, index) => {
                        const {status: shipmentStatus, message: shipmentMessage} = getOrderStatus(
                            shipment?.Status,
                        )

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                        const {status: returnStatus, message: returnMessage} = getOrderStatus(
                            shipment?.currentReturnedOrder?.Status,
                            true,
                        )
                        const currentReturnedOrderStatusOMS =
                            shipment?.currentReturnedOrder?.Status?.toLowerCase()

                        const translatedShipmentStatus =
                            shipmentMessage && formatMessage(shipmentMessage)

                        const hasReturns = !!shipment?.currentReturnedOrder
                        const invoiceUrl = shipment?.fiscalDocuments?.documentUrl
                        const invoiceName =
                            shipment?.fiscalDocuments?.name || `Invoice_Shipment_${index + 1}.pdf`
                        const returnLabel = shipment?.currentReturnedOrder?.returnLabel
                        const returnTracking = shipment?.currentReturnedOrder?.TrackingURL__c
                        const barcode = shipment?.currentReturnedOrder?.barcode
                        // Helper: check if an item has (any) declined return associated
                        const itemHasDeclinedReturn = (itemId) =>
                            order?.returnOrder?.some(
                                (r) =>
                                    r?.Status?.toLowerCase() === SFCC_ORDER_STATUS.DECLINED &&
                                    r?.ReturnOrderLineItems?.records?.some(
                                        (li) => li.OrderItemSummaryId === itemId,
                                    ),
                            )
                        // Compute if there is at least one item still returnable (date window etc.) AND without a declined return
                        const hasReturnableItemsWithoutDeclined = shipment?.orderItems.some(
                            (item) =>
                                item.QuantityAvailableToReturn >= 1 &&
                                !itemHasDeclinedReturn(item.Id) &&
                                item.deliveryDate &&
                                today >= addDays(minimumDaysReturn, item.deliveryDate) &&
                                today <= addDays(maximumDaysReturn, item.deliveryDate),
                        )

                        const {returnedItems, nonReturtableItems} = shipment.orderItems.reduce(
                            (acc, item) => {
                                // Flatten all return line items
                                const allReturnLineItems =
                                    order?.returnOrder?.flatMap(
                                        (returnOrder) =>
                                            returnOrder?.ReturnOrderLineItems?.records || [],
                                    ) || []

                                // If there is exactly one return order, sum ALL line items that share the same OrderItemSummaryId
                                // Else (multiple return orders) keep previous behavior (first match only) to avoid cross-return summing
                                let matchingReturnLineItems = []
                                if (order?.returnOrder?.length === 1) {
                                    matchingReturnLineItems = allReturnLineItems.filter(
                                        (x) => x.OrderItemSummaryId === item.Id,
                                    )
                                } else {
                                    const single = allReturnLineItems.find(
                                        (x) => x.OrderItemSummaryId === item.Id,
                                    )
                                    if (single) matchingReturnLineItems = [single]
                                }

                                const hasDeclined = itemHasDeclinedReturn(item.Id)
                                const isReturnable = matchingReturnLineItems.length > 0

                                if (isReturnable) {
                                    const aggregatedQuantityExpected =
                                        matchingReturnLineItems.reduce(
                                            (sum, li) => sum + (li.QuantityExpected || 0),
                                            0,
                                        )
                                    const aggregatedQuantityReturned =
                                        matchingReturnLineItems.reduce(
                                            (sum, li) => sum + (li.QuantityReturned || 0),
                                            0,
                                        )
                                    acc.returnedItems.push({
                                        ...item,
                                        // Prefer aggregated expected, fallback to initiated if both zero (preserves legacy logic)
                                        QuantityExpected:
                                            aggregatedQuantityExpected ||
                                            item?.QuantityReturnInitiated ||
                                            0,
                                        // Expose aggregated returned (in case future logic needs it)
                                        QuantityReturned: aggregatedQuantityReturned,
                                    })
                                    // Only show in "nonReturtableItems" (i.e. still requestable) if there is remaining qty QuantityAvailableToReturn AND no declined return
                                    if (item.QuantityAvailableToReturn > 0 && !hasDeclined) {
                                        acc.nonReturtableItems.push(item)
                                    }
                                } else {
                                    // Not part of any return yet AND not declined
                                    if (!hasDeclined) {
                                        acc.nonReturtableItems.push(item)
                                    }
                                }
                                return acc
                            },
                            {returnedItems: [], nonReturtableItems: []},
                        )
                        // Check if any item in this shipment is preorder/backorder
                        const preorderBackorderItem = shipment.orderItems?.find(
                            (item) =>
                                item?.PurchaseType__c === 'Preorder' ||
                                item?.PurchaseType__c === 'Backorder',
                        )
                        const isShipmentShippedOrDelivered =
                            shipmentStatus === SFCC_ORDER_STATUS.SHIPPED ||
                            shipmentStatus === SFCC_ORDER_STATUS.DELIVERED
                        const showPreorderBackorderLabel =
                            preorderBackorderItem?.ExpectedAvailabilityDate__c &&
                            !isShipmentShippedOrDelivered

                        return (
                            <Flex key={index} {...styles.multiShipmentContainer}>
                                <Flex {...styles.shipmentSequenceContainer} data-cs-mask>
                                    <Text {...styles.sequenceTextTitle}>
                                        <FormattedMessage
                                            id="account_order_detail.label.shipments"
                                            defaultMessage="Shipment {currentShipment} of {shipmentsLength}"
                                            values={{
                                                currentShipment: index + 1,
                                                shipmentsLength: shipments?.length,
                                            }}
                                        />
                                    </Text>
                                    <Flex gap={remCalc(8)} alignItems={'center'}>
                                        <InformativeLabel
                                            label={translatedShipmentStatus}
                                            width="fit-content"
                                            variant={getInfoLabelVariantByOmsStatus(shipmentStatus)}
                                        />
                                        {showPreorderBackorderLabel && (
                                            <Text {...styles.preorderLabel}>
                                                <FormattedMessage
                                                    id="order_summary.message.available_from"
                                                    defaultMessage="This item will be available from {date}"
                                                    values={{
                                                        date: (
                                                            <Text as="span" fontWeight="bold">
                                                                {formatDate(
                                                                    new Date(
                                                                        preorderBackorderItem?.ExpectedAvailabilityDate__c,
                                                                    ),
                                                                    {
                                                                        day: 'numeric',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                    },
                                                                )}
                                                            </Text>
                                                        ),
                                                    }}
                                                />
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                                {shipmentStatus !== SFCC_ORDER_STATUS.PROCESSING && (
                                    <Flex
                                        gap={{base: remCalc(8), md: remCalc(10)}}
                                        flexDirection={{base: 'column', md: 'row'}}
                                    >
                                        {shipment?.FulfillmentOrderLineItems?.records?.some(
                                            (record) =>
                                                record?.TrackingURL__c && record?.TrackingNumber__c,
                                        ) && (
                                            <Button
                                                variant="primary"
                                                as="a"
                                                href={
                                                    shipment?.FulfillmentOrderLineItems?.records?.find(
                                                        (record) =>
                                                            record?.TrackingURL__c &&
                                                            record?.TrackingNumber__c,
                                                    )?.TrackingURL__c +
                                                    shipment?.FulfillmentOrderLineItems?.records?.find(
                                                        (record) =>
                                                            record?.TrackingURL__c &&
                                                            record?.TrackingNumber__c,
                                                    )?.TrackingNumber__c
                                                }
                                                target="_blank"
                                                width={{base: '100%', md: 'fit-content'}}
                                            >
                                                <FormattedMessage
                                                    id="order_details_track_your_order"
                                                    defaultMessage="Track your order"
                                                />
                                            </Button>
                                        )}
                                        {((shipmentStatus === SFCC_ORDER_STATUS.SHIPPED &&
                                            returnSettings === 'Courier not integrated') ||
                                            shipmentStatus === SFCC_ORDER_STATUS.DELIVERED) &&
                                            returnsAllowed?.[deliveryCountryCode] !== 'No return' &&
                                            hasReturnableItemsWithoutDeclined && (
                                                <Button
                                                    variant="primary"
                                                    width={{base: '100%', md: 'fit-content'}}
                                                    onClick={() => {
                                                        setActiveShipment(
                                                            dispatch,
                                                            shipment.shipmentId,
                                                        )
                                                        window.scrollTo(0, 0)
                                                    }}
                                                >
                                                    <FormattedMessage
                                                        id="order_details_request_return"
                                                        defaultMessage="Request return"
                                                    />
                                                </Button>
                                            )}
                                        {invoiceUrl && (
                                            <Button
                                                {...styles.secondaryButtonOrder}
                                                as={Link}
                                                variant="secondary"
                                                href={invoiceUrl}
                                                download={invoiceName}
                                                width={{base: '100%', md: 'fit-content'}}
                                            >
                                                <FormattedMessage
                                                    id="order_details_download_invoice"
                                                    defaultMessage="Download invoice"
                                                />
                                            </Button>
                                        )}
                                    </Flex>
                                )}
                                <Flex {...styles.multiShipmentContainerNoGap}>
                                    {hasReturns &&
                                        // Hide AlertMessage when returnsAllowed === 'Courier not integrated'
                                        returnsAllowed?.[deliveryCountryCode]?.toLowerCase() !==
                                            'courier not integrated' &&
                                        (returnStatus === SFCC_ORDER_STATUS.COMPLETED ||
                                            shipment?.currentReturnedOrder?.Status ===
                                                SFCC_ORDER_STATUS.DECLINED ||
                                            (shipmentStatus === SFCC_ORDER_STATUS.DELIVERED &&
                                                // Case 1: For non-barcode returns
                                                (((currentReturnedOrderStatusOMS === 'processing' ||
                                                    currentReturnedOrderStatusOMS ===
                                                        'ready for export') &&
                                                    !returnLabel &&
                                                    returnsAllowed?.[
                                                        deliveryCountryCode
                                                    ]?.toLowerCase() !== 'barcode') ||
                                                    // Case 2: For barcode returns
                                                    ((currentReturnedOrderStatusOMS ===
                                                        'processing' ||
                                                        currentReturnedOrderStatusOMS ===
                                                            'ready for export') &&
                                                        !barcode &&
                                                        returnsAllowed?.[
                                                            deliveryCountryCode
                                                        ]?.toLowerCase() === 'barcode')))) &&
                                        // Hide AlertMessage if return type is missed delivery
                                        shipment?.currentReturnedOrder?.ReturnType__c?.toLowerCase() !==
                                            'missed delivery' && (
                                            <AlertMessage
                                                status={
                                                    shipmentStatus ===
                                                        SFCC_ORDER_STATUS.DELIVERED &&
                                                    // Case 1: For non-barcode returns
                                                    (((currentReturnedOrderStatusOMS ===
                                                        'processing' ||
                                                        currentReturnedOrderStatusOMS ===
                                                            'ready for export') &&
                                                        !returnLabel &&
                                                        returnsAllowed?.[
                                                            deliveryCountryCode
                                                        ]?.toLowerCase() !== 'barcode') ||
                                                        // Case 2: For barcode returns
                                                        ((currentReturnedOrderStatusOMS ===
                                                            'processing' ||
                                                            currentReturnedOrderStatusOMS ===
                                                                'ready for export') &&
                                                            !barcode &&
                                                            returnsAllowed?.[
                                                                deliveryCountryCode
                                                            ]?.toLowerCase() === 'barcode'))
                                                        ? 'warning'
                                                        : shipment?.currentReturnedOrder?.Status ===
                                                          SFCC_ORDER_STATUS.DECLINED
                                                        ? 'warning'
                                                        : shipmentStatus ===
                                                              SFCC_ORDER_STATUS.DELIVERED &&
                                                          returnStatus ===
                                                              SFCC_ORDER_STATUS.COMPLETED
                                                        ? 'info'
                                                        : 'info'
                                                }
                                                description={
                                                    shipmentStatus ===
                                                        SFCC_ORDER_STATUS.DELIVERED &&
                                                    // Case 1: For non-barcode returns
                                                    (((currentReturnedOrderStatusOMS ===
                                                        'processing' ||
                                                        currentReturnedOrderStatusOMS ===
                                                            'ready for export') &&
                                                        !returnLabel &&
                                                        returnsAllowed?.[
                                                            deliveryCountryCode
                                                        ]?.toLowerCase() !== 'barcode') ||
                                                        // Case 2: For barcode returns
                                                        ((currentReturnedOrderStatusOMS ===
                                                            'processing' ||
                                                            currentReturnedOrderStatusOMS ===
                                                                'ready for export') &&
                                                            !barcode &&
                                                            returnsAllowed?.[
                                                                deliveryCountryCode
                                                            ]?.toLowerCase() === 'barcode'))
                                                        ? formatMessage({
                                                              defaultMessage:
                                                                  'We will send you an email shortly with instructions on how to proceed.',
                                                              id: 'order_details_return_alert_label.processing.request',
                                                          })
                                                        : shipment?.currentReturnedOrder?.Status ===
                                                          SFCC_ORDER_STATUS.DECLINED
                                                        ? formatMessage({
                                                              defaultMessage:
                                                                  'For more information, contact customer service.',
                                                              id: 'order_details_return_alert_label.contact.customer.service',
                                                          })
                                                        : shipmentStatus ===
                                                              SFCC_ORDER_STATUS.DELIVERED &&
                                                          returnStatus ===
                                                              SFCC_ORDER_STATUS.COMPLETED
                                                        ? formatMessage({
                                                              defaultMessage:
                                                                  'The refund may take a few days to be processed.',
                                                              id: 'order_details_return_alert_label.refund.may.take.a.few.days',
                                                          })
                                                        : (shipmentStatus ===
                                                              SFCC_ORDER_STATUS.SHIPPED ||
                                                              shipmentStatus ===
                                                                  SFCC_ORDER_STATUS.COMPLETED) &&
                                                          returnStatus ===
                                                              SFCC_ORDER_STATUS.COMPLETED &&
                                                          shipment?.currentReturnedOrder
                                                              ?.ReturnType__c === 'Refund Only'
                                                        ? formatMessage({
                                                              defaultMessage:
                                                                  'The refund may take a few days to be processed.',
                                                              id: 'order_details_return_alert_label.refund.may.take.a.few.days',
                                                          })
                                                        : null
                                                }
                                                title={
                                                    shipmentStatus ===
                                                        SFCC_ORDER_STATUS.DELIVERED &&
                                                    // Case 1: For non-barcode returns
                                                    (((currentReturnedOrderStatusOMS ===
                                                        'processing' ||
                                                        currentReturnedOrderStatusOMS ===
                                                            'ready for export') &&
                                                        !returnLabel &&
                                                        returnsAllowed?.[
                                                            deliveryCountryCode
                                                        ]?.toLowerCase() !== 'barcode') ||
                                                        // Case 2: For barcode returns
                                                        ((currentReturnedOrderStatusOMS ===
                                                            'processing' ||
                                                            currentReturnedOrderStatusOMS ===
                                                                'ready for export') &&
                                                            !barcode &&
                                                            returnsAllowed?.[
                                                                deliveryCountryCode
                                                            ]?.toLowerCase() === 'barcode'))
                                                        ? formatMessage({
                                                              defaultMessage:
                                                                  'We are processing your return request',
                                                              id: 'order_details_return_alert_label.processing.title',
                                                          })
                                                        : null
                                                }
                                            />
                                        )}
                                    {shipment.allreturnOrders.length > 0 &&
                                        shipment?.allreturnOrders.map((shipment2) => {
                                            const {status: returnStatus, message: returnMessage} =
                                                getOrderStatus(shipment2?.Status, true)
                                            const translatedReturnStatus =
                                                returnMessage && formatMessage(returnMessage)
                                            return (
                                                <Flex
                                                    {...styles.orderDetailsReturnContainer}
                                                    key={shipment2?.ReturnOrderNumber}
                                                >
                                                    <Flex {...styles.orderDetailsReturnHeader}>
                                                        {shipment2?.ReturnOrderNumber && (
                                                            <Flex {...styles.returnNumberContainer}>
                                                                <Text textStyle="head-small">
                                                                    <FormattedMessage
                                                                        id="order_details_return_number"
                                                                        defaultMessage="<b>Return number: {returnNumber}</b>"
                                                                        values={{
                                                                            returnNumber:
                                                                                shipment2?.ReturnOrderNumber,
                                                                            b: (boldString) => (
                                                                                <b>{boldString}</b>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Text>
                                                                <InformativeLabel
                                                                    label={translatedReturnStatus}
                                                                    width="fit-content"
                                                                    variant={getInfoLabelVariantByOmsStatus(
                                                                        returnStatus,
                                                                    )}
                                                                    height={remCalc(24)}
                                                                />
                                                            </Flex>
                                                        )}
                                                        {shipment2?.CreatedDate && (
                                                            <Text textStyle="body-medium">
                                                                <FormattedMessage
                                                                    id="order_details_return_date"
                                                                    defaultMessage="<b>Date:</b> {returnDate}"
                                                                    values={{
                                                                        returnDate: formatDate(
                                                                            new Date(
                                                                                shipment2?.CreatedDate,
                                                                            ),
                                                                            {
                                                                                year: 'numeric',
                                                                                month: '2-digit',
                                                                                day: '2-digit',
                                                                            },
                                                                        ),
                                                                        b: (boldString) => (
                                                                            <b>{boldString}</b>
                                                                        ),
                                                                    }}
                                                                />
                                                            </Text>
                                                        )}
                                                        {returnsAllowed?.[
                                                            deliveryCountryCode
                                                        ]?.toLowerCase() !==
                                                            'courier not integrated' &&
                                                            shipment2?.TrackingURL__c && (
                                                                <Button
                                                                    variant="primary"
                                                                    as="a"
                                                                    href={shipment2.TrackingURL__c}
                                                                    target="_blank"
                                                                    width={{
                                                                        base: '100%',
                                                                        md: 'fit-content',
                                                                    }}
                                                                >
                                                                    <FormattedMessage
                                                                        id="order_details_track_your_return"
                                                                        defaultMessage="Track your return"
                                                                    />
                                                                </Button>
                                                            )}
                                                        {shipment2?.Status ===
                                                            SFCC_ORDER_STATUS.DECLINED && (
                                                            <>
                                                                <Flex gap={remCalc(8)}>
                                                                    <Text fontWeight="bold">
                                                                        <FormattedMessage
                                                                            id="order_details_return_rejected_info_label"
                                                                            defaultMessage="Return:"
                                                                        />
                                                                    </Text>
                                                                    <InformativeLabel
                                                                        label={'rejected'}
                                                                        width="fit-content"
                                                                        variant={getInfoLabelVariantByOmsStatus(
                                                                            'declined',
                                                                        )}
                                                                    />
                                                                </Flex>
                                                            </>
                                                        )}
                                                        <Divider pt={remCalc(4)} />
                                                    </Flex>

                                                    <ItemList
                                                        items={sortOrderItemsWithWarranty(
                                                            returnedItems.filter((item) =>
                                                                shipment2.ReturnOrderLineItems?.records?.some(
                                                                    (rec) =>
                                                                        rec.OrderItemSummaryId ===
                                                                        item.Id,
                                                                ),
                                                            ),
                                                        )}
                                                        renderItem={(item, index) => {
                                                            // Track if the return item has a status of "partially completed"
                                                            const declined =
                                                                shipment2?.Status?.toLowerCase() ===
                                                                'partially completed'
                                                            // Track if the return item has a QuantityReturned of 0
                                                            // (which means that the item return requested has failed)
                                                            // If so, print the declined label on the OrderItemCard
                                                            const matchingReturnLineItem =
                                                                shipment2?.ReturnOrderLineItems?.records?.find(
                                                                    (lineItem) =>
                                                                        lineItem.OrderItemSummaryId ===
                                                                            item.Id &&
                                                                        lineItem.QuantityReturned ===
                                                                            0,
                                                                )

                                                            return (
                                                                <Box key={item?.Name || index}>
                                                                    <OrderItemCard
                                                                        item={item}
                                                                        isMultipleShipment={true}
                                                                        returnRequested
                                                                        isItemReturnDeclined={
                                                                            declined &&
                                                                            matchingReturnLineItem
                                                                        }
                                                                        isReturnOrder={true}
                                                                        showDivider={
                                                                            index <
                                                                            returnedItems.length - 1
                                                                        }
                                                                    />
                                                                </Box>
                                                            )
                                                        }}
                                                    />

                                                    {shipmentStatus !==
                                                        SFCC_ORDER_STATUS.PROCESSING &&
                                                        shipment2?.ReturnType__c?.toLowerCase() !==
                                                            'refund only' &&
                                                        shipment2?.ReturnType__c?.toLowerCase() !==
                                                            'missed delivery' && (
                                                            <Flex
                                                                {...styles.orderDetailsReturnBottomWrapper}
                                                            >
                                                                <Flex
                                                                    {...styles.orderDetailsReturnBox}
                                                                >
                                                                    <Button
                                                                        variant="secondary"
                                                                        width="fit-content"
                                                                        onClick={() =>
                                                                            generateReturnFormPdf(
                                                                                shipment2,
                                                                                order,
                                                                                formatMessage,
                                                                                formatDate,
                                                                            )
                                                                        }
                                                                        {...styles.printButton}
                                                                    >
                                                                        <FormattedMessage
                                                                            id="order_details_print_the_return_form"
                                                                            defaultMessage="Print the return form"
                                                                        />
                                                                    </Button>
                                                                    {shipment2?.returnLabel &&
                                                                        shipment2?.ReturnType__c !==
                                                                            'Refund Only' &&
                                                                        returnsAllowed?.[
                                                                            deliveryCountryCode
                                                                        ]?.toLowerCase() !==
                                                                            'barcode' &&
                                                                        returnsAllowed?.[
                                                                            deliveryCountryCode
                                                                        ]?.toLowerCase() !==
                                                                            'courier not integrated' && (
                                                                            <Button
                                                                                variant="primary"
                                                                                as={Link}
                                                                                width="fit-content"
                                                                                onClick={() =>
                                                                                    handleDownloadReturnLabel(
                                                                                        shipment2.returnLabel,
                                                                                    )
                                                                                }
                                                                                {...styles.printButton}
                                                                            >
                                                                                <FormattedMessage
                                                                                    id="order_details_print_the_return_label"
                                                                                    defaultMessage="Print the return label"
                                                                                />
                                                                            </Button>
                                                                        )}
                                                                    {returnsAllowed?.[
                                                                        deliveryCountryCode
                                                                    ]?.toLowerCase() ===
                                                                        'barcode' &&
                                                                        shipment2?.barcode && (
                                                                            <Button
                                                                                variant="primary"
                                                                                width="fit-content"
                                                                                onClick={() =>
                                                                                    handleDownloadBarcode(
                                                                                        shipment2.barcode,
                                                                                    )
                                                                                }
                                                                                {...styles.printButton}
                                                                            >
                                                                                <FormattedMessage
                                                                                    id="order_details_print_the_barcode"
                                                                                    defaultMessage="Print barcode"
                                                                                />
                                                                            </Button>
                                                                        )}
                                                                </Flex>
                                                                {shipment2?.ReturnType__c?.toLowerCase() !==
                                                                    'refund only' &&
                                                                    shipment2?.ReturnType__c?.toLowerCase() !==
                                                                        'missed delivery' && (
                                                                        <Button
                                                                            as={Link}
                                                                            {...styles.tertiaryButton}
                                                                            onClick={
                                                                                handleOpenModal
                                                                            }
                                                                        >
                                                                            <FormattedMessage
                                                                                id="account_order_detail.label.info_link"
                                                                                defaultMessage="How does it work?"
                                                                            />
                                                                        </Button>
                                                                    )}
                                                            </Flex>
                                                        )}
                                                </Flex>
                                            )
                                        })}
                                </Flex>
                                <ReturnOrderModal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    returnTracking={returnTracking}
                                />
                                <Flex {...styles.orderDetailsNonReturnableItemsContainer}>
                                    <ItemList
                                        items={nonReturtableItems}
                                        renderItem={(item, index) => (
                                            <OrderItemCard
                                                key={item?.Name || index}
                                                item={item}
                                                isMultipleShipment={true}
                                            />
                                        )}
                                    />
                                </Flex>
                                {shipment.orderItems.some(
                                    (item) =>
                                        !order?.returnOrder?.some((returnOrder) =>
                                            returnOrder?.ReturnOrderLineItems?.records.some(
                                                (x) =>
                                                    x.OrderItemSummaryId === item.Id &&
                                                    x.QuantityExpected >= item.Quantity,
                                            ),
                                        ) &&
                                        item.deliveryDate &&
                                        (today <= addDays(minimumDaysReturn, item.deliveryDate) ||
                                            today >= addDays(maximumDaysReturn, item.deliveryDate)),
                                ) && (
                                    <Flex {...styles.expiredReturnContainer}>
                                        <Flex {...styles.expiredReturnLabelContainer}>
                                            <Text {...styles.expiredReturnTitle}>
                                                <FormattedMessage
                                                    id="account_order_detail.expired_return.title"
                                                    defaultMessage="Customer Service"
                                                />
                                            </Text>
                                            <Text {...styles.expiredReturnDescription}>
                                                <FormattedMessage
                                                    id="account_order_detail.label.expired_return.description"
                                                    defaultMessage="If you need to return an unused item, you can do so within 30 days for a refund of the purchase price, as required. If your order is outside this 30-day period and you have questions or issues with your product, contact our Customer Service team or check our FAQs for immediate assistance on most requests."
                                                />
                                            </Text>
                                        </Flex>
                                        <Flex {...styles.expiredReturnButtonsContainer}>
                                            <Button
                                                as={ReactLink}
                                                to={buildUrl(routingObject.CONTACT_US_PATH)}
                                                variant="primary"
                                                {...styles.expiredReturnButton}
                                            >
                                                <FormattedMessage
                                                    id="home.link.contact_us"
                                                    defaultMessage="Contact us"
                                                />
                                            </Button>
                                            <Button
                                                as={ReactLink}
                                                to={buildUrl(routingObject.FAQ_PATH)}
                                                variant="secondary"
                                                {...styles.expiredReturnButton}
                                            >
                                                <FormattedMessage
                                                    id="account_order_detail.label.expired_return.secondButton"
                                                    defaultMessage="Read FAQs"
                                                />
                                            </Button>
                                            <Button
                                                onClick={handleReturnPolicy}
                                                variant="secondary"
                                                {...styles.expiredReturnButton}
                                            >
                                                <FormattedMessage
                                                    id="account_order_detail.label.expired_return.thirdButton"
                                                    defaultMessage="Go to return policy"
                                                />
                                            </Button>
                                        </Flex>
                                    </Flex>
                                )}
                            </Flex>
                        )
                    })}
                    {unassignedOrderItems.length > 0 && (
                        <Flex key="unassigned-items" {...styles.multiShipmentContainer}>
                            {unassignedPreorderItem && (
                                <Flex {...styles.shipmentSequenceContainer}>
                                    <Text {...styles.preorderLabel}>
                                        <FormattedMessage
                                            id="order_summary.message.available_from"
                                            defaultMessage="This item will be available from {date}"
                                            values={{
                                                date: (
                                                    <Text as="span" fontWeight="bold">
                                                        {formatDate(
                                                            new Date(
                                                                unassignedPreorderItem?.ExpectedAvailabilityDate__c,
                                                            ),
                                                            {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric',
                                                            },
                                                        )}
                                                    </Text>
                                                ),
                                            }}
                                        />
                                    </Text>
                                </Flex>
                            )}
                            <Flex {...styles.orderDetailsNonReturnableItemsContainer}>
                                <ItemList
                                    items={unassignedOrderItems}
                                    renderItem={(item, index) => (
                                        <OrderItemCard
                                            key={item?.Name || index}
                                            item={item}
                                            isMultipleShipment={true}
                                            showDivider={index < unassignedOrderItems.length - 1}
                                        />
                                    )}
                                />
                            </Flex>
                        </Flex>
                    )}
                    {!activeReturnShipmentId && <PricingDetails order={order} />}
                </Flex>
            )}
        </>
    )
}

MultipleShipments.propTypes = {
    order: PropTypes.object,
    refetchOrder: PropTypes.func,
    fiscalDocuments: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            name: PropTypes.string,
        }),
    ),
}

export default MultipleShipments
